<script setup lang="ts">
import Footer from '@/components/Footer.vue';
import Header from '@/components/Header.vue';
import { Head } from '@inertiajs/vue3';
import { Code, Globe, Mail, TrendingUp, UserPlus, Users } from 'lucide-vue-next';
interface Role {
    icon: any;
    title: string;
    description: string;
    status: string;
}

const roles: Role[] = [
    { icon: Code, title: 'Programmers', description: 'Full-stack developers with blockchain expertise', status: 'Actively recruiting' },
    { icon: Globe, title: 'Web Engineers', description: 'Frontend specialists and UI/UX designers', status: 'Actively recruiting' },
    { icon: TrendingUp, title: 'Influencers', description: 'Marketing specialists and content creators', status: 'Actively recruiting' },
];
</script>

<template>
    <Head title="Team">
        <meta
            head-key="description"
            name="description"
            content="We are actively building our core team to bring Digital Safe to life. If you are interested in our project and want to be part of something revolutionary, please reach out."
        />
        <meta
            name="keywords"
            content="Digital Safe, Building Our Team, What We Offer, Skills We're Looking For, Ready to Join the Team?, Current Team"
        />
    </Head>
    <div class="min-h-screen bg-background">
        <Header />
        <main class="pt-20">
            <!-- Hero Section -->
            <section class="relative overflow-hidden py-20">
                <div class="bg-gradient-subtle absolute inset-0 opacity-50"></div>
                <div class="relative z-10 container mx-auto max-w-4xl px-4 text-center">
                    <h1 class="mb-6 text-4xl font-bold md:text-6xl">
                        Digital Safe <span class="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Team</span>
                    </h1>
                    <p class="text-lg leading-relaxed text-muted-foreground md:text-xl">
                        We are in the process of building a team. We need programmers, web engineers, and influencers.
                    </p>
                </div>
            </section>

            <!-- Team Building Section -->
            <section class="py-20">
                <div class="container mx-auto max-w-4xl px-4">
                    <div class="mb-12 flex items-center space-x-4">
                        <div class="glow flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-hero">
                            <Users class="h-6 w-6 text-primary-foreground" />
                        </div>
                        <h2 class="text-3xl font-bold md:text-4xl">Building Our Team</h2>
                    </div>

                    <div class="glass-card mb-12 p-8 text-center">
                        <div class="glow mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-hero">
                            <UserPlus class="h-12 w-12 text-primary-foreground" />
                        </div>
                        <h3 class="mb-4 text-2xl font-semibold">Team Status: In the Making</h3>
                        <p class="text-lg leading-relaxed text-muted-foreground">
                            We are actively building our core team to bring Digital Safe to life. If you are interested in our project and want to be
                            part of something revolutionary, please reach out.
                        </p>
                    </div>

                    <!-- Roles We're Looking For -->
                    <div class="mb-12 grid grid-cols-1 gap-8 md:grid-cols-3">
                        <div
                            v-for="(role, index) in roles"
                            :key="index"
                            class="glass-card hover:glow glow-hover p-6 text-center transition-all duration-300"
                        >
                            <div class="glow mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-lg bg-gradient-hero">
                                <component :is="role.icon" class="h-8 w-8 text-primary-foreground" />
                            </div>
                            <h3 class="mb-3 text-xl font-semibold">{{ role.title }}</h3>
                            <p class="mb-4 text-sm leading-relaxed text-muted-foreground">{{ role.description }}</p>
                            <div class="rounded-full bg-primary/20 px-3 py-1 text-xs font-semibold text-primary">
                                {{ role.status }}
                            </div>
                        </div>
                    </div>

                    <!-- What We Offer -->
                    <div class="glass-card mb-12 p-8">
                        <h3 class="mb-6 text-center text-2xl font-semibold">What We Offer</h3>
                        <div class="grid grid-cols-1 gap-8 md:grid-cols-2">
                            <div>
                                <h4 class="mb-4 font-semibold text-primary">Innovation & Impact</h4>
                                <ul class="space-y-2 text-muted-foreground">
                                    <li>• Work on a revolutionary financial security platform</li>
                                    <li>• Be part of a patent-registered innovative system</li>
                                    <li>• Contribute to green transformation technology</li>
                                    <li>• Shape the future of digital asset security</li>
                                </ul>
                            </div>
                            <div>
                                <h4 class="mb-4 font-semibold text-primary">Growth & Opportunity</h4>
                                <ul class="space-y-2 text-muted-foreground">
                                    <li>• Join a growing startup with huge potential</li>
                                    <li>• Token-based compensation opportunities</li>
                                    <li>• Remote-first work environment</li>
                                    <li>• Direct impact on product development</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <!-- Skills We Need -->
                    <div class="glass-card p-8">
                        <h3 class="mb-6 text-center text-2xl font-semibold">Skills We're Looking For</h3>
                        <div class="space-y-6">
                            <div>
                                <h4 class="mb-3 font-semibold text-accent">For Programmers:</h4>
                                <div class="flex flex-wrap gap-2">
                                    <span class="rounded-full bg-primary/20 px-3 py-1 text-sm text-primary">Blockchain Development</span>
                                    <span class="rounded-full bg-primary/20 px-3 py-1 text-sm text-primary">Smart Contracts</span>
                                    <span class="rounded-full bg-primary/20 px-3 py-1 text-sm text-primary">React/TypeScript</span>
                                    <span class="rounded-full bg-primary/20 px-3 py-1 text-sm text-primary">Node.js</span>
                                    <span class="rounded-full bg-primary/20 px-3 py-1 text-sm text-primary">Cryptography</span>
                                    <span class="rounded-full bg-primary/20 px-3 py-1 text-sm text-primary">Security</span>
                                </div>
                            </div>
                            <div>
                                <h4 class="mb-3 font-semibold text-accent">For Web Engineers:</h4>
                                <div class="flex flex-wrap gap-2">
                                    <span class="rounded-full bg-secondary/20 px-3 py-1 text-sm text-secondary">UI/UX Design</span>
                                    <span class="rounded-full bg-secondary/20 px-3 py-1 text-sm text-secondary">Frontend Development</span>
                                    <span class="rounded-full bg-secondary/20 px-3 py-1 text-sm text-secondary">Mobile Apps</span>
                                    <span class="rounded-full bg-secondary/20 px-3 py-1 text-sm text-secondary">Responsive Design</span>
                                    <span class="rounded-full bg-secondary/20 px-3 py-1 text-sm text-secondary">Web3 Integration</span>
                                </div>
                            </div>
                            <div>
                                <h4 class="mb-3 font-semibold text-accent">For Influencers:</h4>
                                <div class="flex flex-wrap gap-2">
                                    <span class="rounded-full bg-accent/20 px-3 py-1 text-sm text-accent">Content Creation</span>
                                    <span class="rounded-full bg-accent/20 px-3 py-1 text-sm text-accent">Social Media</span>
                                    <span class="rounded-full bg-accent/20 px-3 py-1 text-sm text-accent">Community Building</span>
                                    <span class="rounded-full bg-accent/20 px-3 py-1 text-sm text-accent">Brand Marketing</span>
                                    <span class="rounded-full bg-accent/20 px-3 py-1 text-sm text-accent">Crypto/Fintech Knowledge</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Join Us Section -->
            <section class="bg-gradient-subtle py-20">
                <div class="container mx-auto max-w-2xl px-4 text-center">
                    <h2 class="mb-8 text-3xl font-bold md:text-4xl">Ready to <span class="text-primary">Join the Team?</span></h2>
                    <p class="mb-8 text-lg leading-relaxed text-muted-foreground">
                        If you are interested in our project and want to be part of building the future of digital asset security, we'd love to hear
                        from you. Send us your portfolio, experience, and why you want to join Digital Safe.
                    </p>

                    <div class="glass-card p-8">
                        <div class="mb-6 flex items-center justify-center space-x-2">
                            <Mail class="h-6 w-6 text-primary" />
                            <h3 class="text-xl font-semibold">Get in Touch</h3>
                        </div>
                        <p class="mb-6 text-muted-foreground">Send your application and questions to:</p>
                        <a
                            href="mailto:peter@digitalsafe.io?subject=Digital Safe Team Application"
                            class="hover:glow inline-flex items-center space-x-2 rounded-lg bg-gradient-hero px-8 py-4 text-lg font-semibold text-primary-foreground transition-all duration-300"
                        >
                            <Mail class="h-5 w-5" />
                            <span>peter@digitalsafe.io</span>
                        </a>

                        <div class="mt-8 rounded-lg bg-muted/20 p-4">
                            <p class="text-sm text-muted-foreground">
                                <strong>Subject line:</strong> Digital Safe Team Application - [Your Role]<br />
                                <strong>Include:</strong> Resume, Portfolio, GitHub/LinkedIn profiles, and a brief note about your interest in our
                                project
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Current Team Section -->
            <section class="py-20">
                <div class="container mx-auto max-w-2xl px-4 text-center">
                    <h2 class="mb-8 text-3xl font-bold md:text-4xl">Current <span class="text-primary">Team</span></h2>
                    <div class="glass-card p-8">
                        <div class="glow mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-hero">
                            <span class="text-2xl font-bold text-primary-foreground">P</span>
                        </div>
                        <h3 class="mb-2 text-xl font-semibold">Peter</h3>
                        <p class="mb-4 text-muted-foreground">Founder & Project Lead</p>
                        <a href="mailto:peter@digitalsafe.io" class="text-primary transition-colors hover:text-accent"> peter@digitalsafe.io </a>

                        <div class="mt-8 text-center">
                            <p class="text-sm text-muted-foreground">
                                Leading the vision and development of Digital Safe, working to build a revolutionary platform for secure digital asset
                                storage and management.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </main>
        <Footer />
    </div>
</template>
